import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  getAiChatBot,
  getAiChatBotSources,
  deleteAiChatBotSource,
  postAiChatBotIngestWebsite,
  getAiChatBotFaqs,
  patchAiChatBotSource,
  getAiPersonas,
  createAiPersona,
  updateAiPersona as updateAiPersonaApi,
  deleteAiPersona as deleteAiPersonaApi,
  createAiPersonaVideo,
  completeAiPersonaVideoUpload,
  generateAiChatbotFaq,
  bulkDeleteAiChatBotSources,
  updateAiChatBotRefreshConfig as patchAiChatBotRefreshConfig,
  createAiChatBotRefreshConfig,
  deleteAiChatBotRefreshConfig,
  bulkDeleteAiChatBotQaSources,
  updateAiChatBotScopeAnswer,
  createAIChatbotQuestionWithAnswer,
  patchAiChatBot,
} from 'shared/api';
import {
  AIChatBot,
  QADataSource,
  WebsiteDataSource,
  PolymorphicAIChatBotDataSource,
  AiChatbotJobStatus,
  AIChatBotRefreshConfig,
  FaqAnswerStatus,
} from 'types/ai-chatbot';
import type { Store } from './index';
import { AIPersona, AIPersonaAdd, AIPersonaVideoType } from 'types/ai-persona';
import { uploadSignedPost } from 'shared/utils/s3';
import { sleep } from 'shared/utils/helpers';
import { AppState } from './reducers';
import { selectActiveUser } from './shared-selectors';
import { message } from 'antd';

export const initAiChatBot = createAsyncThunk(
  'aiChat/initAiChatBot',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await getAiChatBot();
      const chatBot = response.data;

      // Check for any in-progress jobs
      const inProgressJobs = chatBot.jobs.filter(job => job.status === 'in_progress');

      // Start polling for each in-progress job
      inProgressJobs.forEach(job => {
        switch (job.jobType) {
          case 'scrape':
            void dispatch(pollScrapeJob());
            break;
          case 'generate_faq':
            void dispatch(pollGenerateFaqJob());
            break;
        }
      });

      return chatBot;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAllAiChatBotSources = createAsyncThunk(
  'aiChat/fetchAllAiChatBotSources',
  async (id: number, { rejectWithValue }) => {
    try {
      const fetchAllSources = async (
        id: number,
        allSources: PolymorphicAIChatBotDataSource[] = [],
        nextPage = 1,
        perPage = 50,
      ): Promise<WebsiteDataSource[]> => {
        const response = await getAiChatBotSources(id, nextPage, perPage);
        const newSources = response.data.items;
        const updatedSources = [...allSources, ...newSources];

        const totalItems = response.data.total;
        const currentCount = nextPage * perPage;
        const hasMorePages = currentCount < totalItems;

        if (hasMorePages) {
          return fetchAllSources(id, updatedSources, nextPage + 1);
        } else {
          return updatedSources as WebsiteDataSource[];
        }
      };

      const websites = await fetchAllSources(id);
      return websites;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteSource = createAsyncThunk(
  'aiChat/deleteSource',
  async (
    { chatBotId, sourceId }: { chatBotId: number; sourceId: number | string },
    { rejectWithValue },
  ) => {
    try {
      await deleteAiChatBotSource(chatBotId, sourceId);
      return sourceId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteBulkWebsiteSources = createAsyncThunk(
  'aiChat/deleteBulkWebsiteSources',
  async ({ id, sourceIds }: { id: number; sourceIds: number[] }, { rejectWithValue }) => {
    try {
      await bulkDeleteAiChatBotSources(id, sourceIds);
      return sourceIds;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteBulkQaSources = createAsyncThunk(
  'aiChat/deleteBulkQaSources',
  async (
    { id, questionIds }: { id: number; questionIds: number[] },
    { rejectWithValue },
  ) => {
    try {
      await bulkDeleteAiChatBotQaSources(id, questionIds);
      return questionIds;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAndProcessWebsite = createAsyncThunk(
  'aiChat/fetchAndProcessWebsite',
  async (
    { id, urls, crawl = true }: { id: number; urls: string[]; crawl?: boolean },
    { dispatch },
  ) => {
    await postAiChatBotIngestWebsite(id, urls, crawl);
    dispatch(pollScrapeJob());
  },
);

export const generateFaqFromWebsite = createAsyncThunk(
  'aiChat/generateFaqFromWebsite',
  async (id: number, { dispatch }) => {
    await generateAiChatbotFaq(id);
    dispatch(pollGenerateFaqJob());
  },
);

export const fetchAllAiChatBotFaqs = createAsyncThunk(
  'aiChat/fetchAllAiChatBotFaqs',
  async (id: number, { rejectWithValue }) => {
    try {
      const fetchFaqs = async (
        id: number,
        allFaqs: QADataSource[] = [],
        nextPage = 1,
        perPage = 50,
      ): Promise<QADataSource[]> => {
        const response = await getAiChatBotFaqs(id, nextPage, perPage);
        const newFaqs = response.data.items;
        const updatedFaqs = [...allFaqs, ...newFaqs];

        const totalItems = response.data.total;
        const currentCount = nextPage * perPage;
        const hasMorePages = currentCount < totalItems;

        if (hasMorePages) {
          return fetchFaqs(id, updatedFaqs, nextPage + 1, perPage);
        } else {
          return updatedFaqs;
        }
      };

      const faqs = await fetchFaqs(id);
      return faqs;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateQaItem = createAsyncThunk(
  'aiChat/updateQaItem',
  async (
    {
      chatBotId,
      questionId,
      question,
    }: {
      chatBotId: number;
      questionId: number | string;
      question: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const payload = {
        question,
      };
      const response = await patchAiChatBotSource(chatBotId, questionId, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addQaItem = createAsyncThunk(
  'aiChat/addQaItem',
  async (
    {
      chatBotId,
      question,
      answer,
    }: {
      chatBotId: number;
      question: string;
      answer: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await createAIChatbotQuestionWithAnswer(
        chatBotId,
        question,
        answer,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAiPersonas = createAsyncThunk(
  'aiChat/fetchAiPersonas',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAiPersonas();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAiPersona = createAsyncThunk(
  'aiChat/addAiPersona',
  async (data: AIPersonaAdd, { rejectWithValue }) => {
    try {
      const response = await createAiPersona(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAiPersona = createAsyncThunk(
  'aiChat/updateAiPersona',
  async ({ id, data }: { id: number; data: Partial<AIPersona> }, { rejectWithValue }) => {
    try {
      const response = await updateAiPersonaApi(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteAiPersona = createAsyncThunk(
  'aiChat/deleteAiPersona',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteAiPersonaApi(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAiPersonaVideo = createAsyncThunk(
  'aiChat/addAiPersonaVideo',
  async (
    data: {
      file: Blob;
      aiPersonaId: number;
      videoType: AIPersonaVideoType;
      mimetype: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await createAiPersonaVideo({
        aiPersonaId: data.aiPersonaId,
        videoType: data.videoType,
        mimetype: data.mimetype,
      });

      await uploadSignedPost(data.file, response.data.data);

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const completeUpload = createAsyncThunk(
  'aiChat/completeAiPersonaVideoUpload',
  async (personaId: number, { rejectWithValue }) => {
    try {
      const response = await completeAiPersonaVideoUpload(personaId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const pollScrapeJob = createAsyncThunk(
  'aiChat/pollScrapeJob',
  async (_, { dispatch, getState }) => {
    const state = getState() as AppState;
    if (state.aiChat.activePollingJobs.scrape) {
      return; // Skip if already polling
    }

    dispatch(setPollingJob({ type: 'scrape', active: true }));
    let status: AiChatbotJobStatus = 'in_progress';
    try {
      while (status === 'in_progress') {
        const chatBot = (await getAiChatBot()).data;
        dispatch(setAiChatBot(chatBot));
        status =
          chatBot.jobs.find(job => job.jobType === 'scrape')?.status || 'in_progress';
        if (status !== 'in_progress') {
          if (status === 'error') {
            message.error('Failed to process website');
          } else {
            message.success('Website processed successfully');
            await dispatch(fetchAllAiChatBotSources(chatBot.id));
          }
          break;
        }
        await sleep(3000);
      }
    } finally {
      dispatch(setPollingJob({ type: 'scrape', active: false }));
    }
  },
);

export const pollGenerateFaqJob = createAsyncThunk(
  'aiChat/pollGenerateFaqJob',
  async (_, { dispatch, getState }) => {
    const state = getState() as AppState;
    if (state.aiChat.activePollingJobs.generateFaq) {
      return; // Skip if already polling
    }

    dispatch(setPollingJob({ type: 'generateFaq', active: true }));
    let status: AiChatbotJobStatus = 'in_progress';
    try {
      while (status === 'in_progress') {
        const chatBot = (await getAiChatBot()).data;
        dispatch(setAiChatBot(chatBot));
        status =
          chatBot.jobs.find(job => job.jobType === 'generate_faq')?.status ||
          'in_progress';
        if (status !== 'in_progress') {
          if (status === 'error') {
            message.error('Failed to generate AI questions and answers');
          } else {
            message.success('AI questions and answers generated successfully');
            await dispatch(fetchAllAiChatBotFaqs(chatBot.id));
          }
          break;
        }
        await sleep(3000);
      }
    } finally {
      dispatch(setPollingJob({ type: 'generateFaq', active: false }));
    }
  },
);

export const updateAiChatBotRefreshConfig = createAsyncThunk(
  'aiChat/updateAiChatBotRefreshConfig',
  async (config: AIChatBotRefreshConfig, { rejectWithValue }) => {
    const { id, ...updates } = config;
    try {
      const response = await (id
        ? patchAiChatBotRefreshConfig(id, updates)
        : createAiChatBotRefreshConfig(updates));

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const removeAiChatBotRefreshConfig = createAsyncThunk(
  'aiChat/removeAiChatBotRefreshConfig',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteAiChatBotRefreshConfig(id);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateScopeAnswer = createAsyncThunk(
  'aiChat/updateScopeAnswer',
  async (
    {
      answerId,
      status,
      answer,
    }: { answerId: number; status: 'APPROVED' | 'REJECTED' | 'PENDING'; answer?: string },
    { rejectWithValue, getState },
  ) => {
    const state = getState() as AppState;
    const chatBot = state.aiChat.aiChatBot;
    if (!chatBot) {
      return rejectWithValue('Chatbot not found');
    }

    try {
      const response = await updateAiChatBotScopeAnswer(
        chatBot.id,
        answerId,
        status,
        answer,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAiChatBot = createAsyncThunk(
  'aiChat/updateAiChatBot',
  async (
    { id, updates }: { id: number; updates: Partial<AIChatBot> },
    { rejectWithValue },
  ) => {
    try {
      const response = await patchAiChatBot(id, updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export type AiChatState = {
  aiChatBot: AIChatBot | null;
  websites: WebsiteDataSource[] | undefined;
  qa: QADataSource[] | undefined;
  aiPersonas: AIPersona[];
  isChatBotLoading: boolean;
  isChatBotSourcesLoading: boolean;
  isAiPersonasLoading: boolean;
  error: string | null;
  generatedAnswer: {
    question: string;
    answer: string;
    status: 'success' | 'error';
  } | null;
  activePollingJobs: {
    scrape: boolean;
    generateFaq: boolean;
  };
};

const initialState: AiChatState = {
  aiChatBot: null,
  websites: undefined,
  qa: undefined,
  aiPersonas: [],
  isChatBotLoading: false,
  isChatBotSourcesLoading: false,
  isAiPersonasLoading: false,
  error: null,
  generatedAnswer: null,
  activePollingJobs: {
    scrape: false,
    generateFaq: false,
  },
};

const resetState = (state: AiChatState) => {
  state.aiChatBot = null;
  state.websites = undefined;
  state.qa = undefined;
  state.aiPersonas = [];
  state.isChatBotLoading = false;
  state.isChatBotSourcesLoading = false;
  state.isAiPersonasLoading = false;
  state.error = null;
  state.generatedAnswer = null;
};

const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    setGeneratedAnswer: (state, action) => {
      state.generatedAnswer = action.payload;
    },
    setAiChatBot: (state, action) => {
      state.aiChatBot = action.payload;
    },
    setPollingJob: (
      state,
      action: { payload: { type: 'scrape' | 'generateFaq'; active: boolean } },
    ) => {
      state.activePollingJobs[action.payload.type] = action.payload.active;
    },
  },
  extraReducers: builder => {
    builder
      // initAiChatBot
      .addCase(initAiChatBot.pending, state => {
        state.isChatBotLoading = true;
      })
      .addCase(initAiChatBot.fulfilled, (state, action) => {
        resetState(state);
        state.aiChatBot = action.payload;
      })
      .addCase(initAiChatBot.rejected, (state, action) => {
        resetState(state);
        state.error = action.payload as string;
      })

      // fetchAllAiChatBotSources
      .addCase(fetchAllAiChatBotSources.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(fetchAllAiChatBotSources.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.websites = action.payload;
      })
      .addCase(fetchAllAiChatBotSources.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.payload as string;
      })

      // deleteSource
      .addCase(deleteSource.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(deleteSource.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.websites = state.websites?.filter(website => website.id !== action.payload);
        state.qa = state.qa?.filter(qaItem => qaItem.id !== action.payload);
      })
      .addCase(deleteSource.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.payload as string;
      })

      // fetchAndProcessWebsite
      .addCase(fetchAndProcessWebsite.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(fetchAndProcessWebsite.fulfilled, state => {
        state.isChatBotSourcesLoading = false;
      })
      .addCase(fetchAndProcessWebsite.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.error.message || 'Failed to fetch and process website';
      })

      // fetchAllAiChatBotFaqs
      .addCase(fetchAllAiChatBotFaqs.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(fetchAllAiChatBotFaqs.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.qa = action.payload;
      })
      .addCase(fetchAllAiChatBotFaqs.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.payload as string;
      })

      // generateFaqFromWebsite
      .addCase(generateFaqFromWebsite.pending, () => {})
      .addCase(generateFaqFromWebsite.fulfilled, () => {})
      .addCase(generateFaqFromWebsite.rejected, () => {})

      // updateQaItem
      .addCase(updateQaItem.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(updateQaItem.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        const updatedQaItem = action.payload;

        state.qa = state.qa?.map(item =>
          item.id === (updatedQaItem as QADataSource)?.id
            ? { ...item, ...updatedQaItem, id: item.id }
            : item,
        );
      })
      .addCase(updateQaItem.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.payload as string;
      })

      // addQaItem
      .addCase(addQaItem.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        const newQaItem = action.payload;
        state.qa = state.qa ? [newQaItem, ...state.qa] : [newQaItem];
      })

      // fetchAiPersonas
      .addCase(fetchAiPersonas.pending, state => {
        state.isAiPersonasLoading = true;
      })
      .addCase(fetchAiPersonas.fulfilled, (state, action) => {
        state.isAiPersonasLoading = false;
        state.aiPersonas = action.payload;
      })
      .addCase(fetchAiPersonas.rejected, state => {
        state.isAiPersonasLoading = false;
      })

      // addAiPersona
      .addCase(addAiPersona.fulfilled, (state, action) => {
        state.isAiPersonasLoading = false;
        state.aiPersonas = [...state.aiPersonas, action.payload];
      })

      // updateAiPersona
      .addCase(updateAiPersona.fulfilled, (state, action) => {
        state.isAiPersonasLoading = false;
        const updatedPersona = action.payload;
        state.aiPersonas = state.aiPersonas.map(persona =>
          persona.id === updatedPersona.id ? updatedPersona : persona,
        );
      })

      // deleteAiPersona
      .addCase(deleteAiPersona.fulfilled, (state, action) => {
        state.aiPersonas = state.aiPersonas.filter(
          persona => persona.id !== action.payload,
        );
      })

      // completeAiPersonaVideoUpload
      .addCase(completeUpload.fulfilled, (state, action) => {
        const updatedPersona = action.payload;
        state.aiPersonas = state.aiPersonas.map(persona =>
          persona.id === updatedPersona.id ? updatedPersona : persona,
        );
      })

      // deleteBulkWebsiteSources
      .addCase(deleteBulkWebsiteSources.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(deleteBulkWebsiteSources.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.websites = state.websites?.filter(
          website => !action.payload.includes(website.id as number),
        );
      })
      .addCase(deleteBulkWebsiteSources.rejected, state => {
        state.isChatBotSourcesLoading = false;
      })

      // deleteBulkQaSources
      .addCase(deleteBulkQaSources.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(deleteBulkQaSources.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.qa = state.qa?.filter(
          qaItem => !action.payload.includes(qaItem.id as number),
        );
      })
      .addCase(deleteBulkQaSources.rejected, state => {
        state.isChatBotSourcesLoading = false;
      })

      .addCase(updateAiChatBotRefreshConfig.fulfilled, (state, action) => {
        if (state.aiChatBot) {
          state.aiChatBot.refreshConfig = action.payload;
        }
      })

      .addCase(removeAiChatBotRefreshConfig.fulfilled, state => {
        if (state.aiChatBot) {
          state.aiChatBot.refreshConfig = null;
        }
      })

      .addCase(updateScopeAnswer.fulfilled, (state, action) => {
        state.qa = state.qa?.map(answer => {
          if (answer.proposedAnswer?.id === action.payload.id) {
            return {
              ...answer,
              proposedAnswer: action.payload,
              ...(action.payload.status === FaqAnswerStatus.APPROVED && {
                answer: action.payload,
              }),
            };
          }

          if (answer.answer?.id === action.payload.id) {
            return {
              ...answer,
              answer: action.payload,
            };
          }

          return answer;
        });
      })

      .addCase(updateAiChatBot.fulfilled, (state, action) => {
        state.aiChatBot = action.payload;
      });
  },
});

export const { setGeneratedAnswer, setAiChatBot, setPollingJob } = aiChatSlice.actions;

export async function initAiChat(store: Store) {
  const initialUser = selectActiveUser(store.getState());
  if (initialUser) {
    store.dispatch(initAiChatBot());
    store.dispatch(fetchAiPersonas());
  }

  let curAuthed = initialUser;
  store.subscribe(() => {
    const prevAuthed = curAuthed;
    curAuthed = selectActiveUser(store.getState());
    const loggedIn = !prevAuthed && curAuthed;
    const changedUser = prevAuthed && curAuthed && prevAuthed.id !== curAuthed.id;

    if (loggedIn || changedUser) {
      store.dispatch(initAiChatBot());
      store.dispatch(fetchAiPersonas());
    }
  });
}

export default aiChatSlice.reducer;

export const selectActiveOrgDefaultPersona = createSelector(
  (state: AppState) => state.aiChat.aiChatBot?.defaultRep,
  (state: AppState) => state.aiChat.aiPersonas,
  (defaultRep, personas) => personas.find(persona => persona.id === defaultRep),
);
